import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getArtistSubscriptionStatus } from "../services/artistSubscriptionService";

interface ProtectedRouteProps {
  children: React.ReactElement;
  roles?: string[]; // e.g., ["Admin"] or ["Artist"]
  requireSubscription?: boolean; // For Artist pages that need active subscription
}

export default function ProtectedRoute({ children, roles, requireSubscription = true }: ProtectedRouteProps) {
  const { token, userRole, subscriptionStatus, isSubscriptionValid, setSubscriptionStatus } = useAuth();
  const location = useLocation();

  // Fetch subscription status for Artist role if not already loaded
  useEffect(() => {
    if (token && userRole === 'Artist' && !subscriptionStatus) {
      getArtistSubscriptionStatus()
        .then(setSubscriptionStatus)
        .catch((error) => {
          console.error('Failed to fetch subscription status:', error);
          // Set null to indicate no subscription
          setSubscriptionStatus(null);
        });
    }
  }, [token, userRole, subscriptionStatus, setSubscriptionStatus]);

  // Not authenticated: send to login, preserve where they were
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change gate: if flagged, redirect to change-password page for any protected route
  const forcePasswordChange =
    typeof window !== 'undefined' && localStorage.getItem('forcePasswordChange') === 'true';
  if (
    forcePasswordChange &&
    location.pathname !== '/auth/force-change-password'
  ) {
    return <Navigate to="/auth/force-change-password" replace />;
  }

  // Role restricted and user doesn't match
  if (roles && userRole && !roles.includes(userRole)) {
    // Try to send the user to their home dashboard based on role
    if (userRole === "Admin") return <Navigate to="/admin/dashboard" replace />;
    if (userRole === "Artist") return <Navigate to="/artist/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }

  // Artist subscription validation (only for Artist role and when requireSubscription is true)
  if (
    userRole === 'Artist' && 
    requireSubscription && 
    location.pathname !== '/artist/subscription-expired'
  ) {
    // Still loading subscription status
    if (subscriptionStatus === null && token) {
      return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
    }
    
    // Check if subscription is valid
    if (!isSubscriptionValid()) {
      return <Navigate to="/artist/subscription-expired" replace />;
    }
  }

  return children;
}
